/**
 * Section 5, rendered from the facts — never from prose. The model does not
 * write this table; it is read off the verified figures by period, with the
 * derived rows (growth, margins) marked "computed" because code computed them.
 *
 * Figures with no period (the balance sheet: cash, debt, working capital)
 * are one number each, not a series, so they sit in their own small table
 * under the years rather than under the first year's column. Which table a
 * row belongs in is the recipe's call, not the period's: a by-year figure the
 * run could not find (EBITDA, "Not found") is stored with no period, and it
 * still belongs with revenue and income, not under the balance sheet (DOP-646).
 *
 * Numbers line up on their digits: a figure that is not in brackets keeps
 * room for the closing bracket a negative one carries.
 *
 * Under it, the notes the firm's memos always carry: that projections are
 * management's, which source a conflicted cell shows and what the other said,
 * the FX rate any converted figure was converted at, and which figures are
 * still in the currency the materials state them in, so a "$mm" table never
 * passes off a Canadian-dollar figure as a US one. When every money figure
 * is in one such currency the header says it once — "C$mm" — and no row is
 * marked (DOP-658); in a mixed table each foreign row says "(C$)". Either
 * way the note says why no conversion was made.
 *
 * The balance sheet is always as of a date, and its header says which: the
 * newest as-of date among its figures ("Balance sheet, as of Dec 31, 2025").
 */

import React from "react";

import type {
	DealScreeningFact,
	DealScreeningRunFigure,
	DealScreeningSource,
} from "../../types/dealScreening";
import {
	NOT_FOUND,
	currencyMark,
	currencyMarkAndCode,
	currencyPrefix,
	formatCalendarDate,
	formatFactValue,
	formatIsoDay,
	formatSourceLabel,
	unconvertedCurrency,
} from "./dealScreeningFormat";
import DerivedFormulaPopover, { indexFacts } from "./DerivedFormulaPopover";
import FactValue from "./FactValue";
import { periodsOf } from "./memoModel";
import { FINANCIAL_COLUMNS, FINANCIAL_ROWS, inSavedOrder, orderSetting } from "./tableOrder";

export interface FinancialSummaryTableProps {
	facts: DealScreeningFact[];
	/** The run's recipe rows: whether a figure is by year or a point reading. */
	figures?: DealScreeningRunFigure[];
	conventions: Record<string, string>;
	onOpenSource: (source: DealScreeningSource) => void;
	/** Opens a person's own answer for editing; absent where editing is not offered. */
	onEditFact?: (fact: DealScreeningFact) => void;
	/** Opens an open conflict on the Gaps tab. */
	onOpenConflict?: (fact: DealScreeningFact) => void;
}

/**
 * The ledger's notes on a figure that has no answer: a margin whose divisor
 * the materials state as nil, a multiple over a loss (DOP-574).
 */
const NO_ANSWER = new Set([
	"Divides by a figure the materials state as nil.",
	"Not meaningful: divides by a negative figure.",
]);

/**
 * Whether a row is an optional figure the materials do not carry. Settings
 * said the memo can do without it, so the table does (DOP-556); a required
 * figure that is missing still reads "Not found", and a margin on a nil
 * divisor still prints its dash.
 */
export function isAbsentOptional(cells: DealScreeningFact[]): boolean {
	return (
		cells.length > 0 &&
		cells.every(
			(fact) =>
				fact.policy === "optional" &&
				(fact.state === "gap" || fact.state === "not_available") &&
				!NO_ANSWER.has(fact.note ?? "") &&
				fact.entered_by_user_id === null,
		)
	);
}

interface FigureRow {
	key: string;
	label: string;
	derived: boolean;
	/** The currency every stated cell is still in, when no rate converted it. */
	currency: string | null;
	/** By period; a point figure has one fact under `null`. */
	cells: Map<string | null, DealScreeningFact>;
}

function figureRows(facts: DealScreeningFact[]): FigureRow[] {
	const rows = new Map<string, FigureRow>();
	[...facts]
		.sort((a, b) => a.sort_order - b.sort_order)
		.forEach((fact) => {
			const row = rows.get(fact.key) ?? {
				key: fact.key,
				label: fact.label,
				derived: fact.kind === "derived",
				currency: null,
				cells: new Map<string | null, DealScreeningFact>(),
			};
			row.cells.set(fact.period, fact);
			rows.set(fact.key, row);
		});
	rows.forEach((row) => {
		const stated = Array.from(row.cells.values()).filter((fact) => fact.value !== null);
		const currencies = new Set(
			stated.map((fact) => (fact.fx_rate === null ? fact.original_currency : null)),
		);
		const [only] = Array.from(currencies);
		row.currency = stated.length > 0 && currencies.size === 1 && only ? only : null;
	});
	return Array.from(rows.values());
}

/**
 * "Revenue FY2025A is shown per the financial statements; the model reports
 * $13.6mm." When the other value sits in the SAME kind of document (a model's
 * summary vs its rollup), the document type says nothing, so the cell or
 * page names it instead: "…per Annual_Financials!E8; Revenue_Rollup!M57
 * reports $39.99mm."
 */
function conflictNote(fact: DealScreeningFact): string {
	const what = fact.period ? `${fact.period} ${fact.label.toLowerCase()}` : fact.label;
	const sameKind = fact.alternates.some(
		(alt) => fact.source && alt.source.doc_type === fact.source.doc_type,
	);
	const name = (source: DealScreeningSource) =>
		sameKind ? formatSourceLabel(source) : `the ${source.doc_type.replace(/_/g, " ")}`;
	const shown = fact.source ? ` per ${name(fact.source)}` : "";
	const others = fact.alternates
		.map(
			(alt) =>
				`${name(alt.source)} reports ${formatFactValue(
					alt.value,
					alt.unit,
					unconvertedCurrency(alt),
				)}`,
		)
		.join("; ");
	return `${what} is shown${shown}; ${others}.`;
}

/**
 * The one currency a table is in when it is not USD: every money figure the
 * materials state is still in it. A figure a person typed carries no currency
 * of its own — it was entered under this table's header — and a figure with
 * no value says nothing. Null for a USD table or a mixed one.
 */
export function tableCurrency(facts: DealScreeningFact[]): string | null {
	const stated = facts.filter(
		(fact) =>
			fact.unit === "usd_m" &&
			fact.value !== null &&
			!(fact.entered_by_user_id !== null && !fact.original_currency),
	);
	const currencies = new Set(stated.map((fact) => unconvertedCurrency(fact)));
	const [only] = Array.from(currencies);
	return stated.length > 0 && currencies.size === 1 && only ? only : null;
}

/** "as of Dec 31, 2025" from the newest as-of date among the rows; null when none is dated. */
export function asOfDate(facts: DealScreeningFact[]): string | null {
	const dates = facts
		.map((fact) => fact.period_end ?? null)
		.filter((date): date is string => date !== null)
		.sort();
	return dates.length > 0 ? formatCalendarDate(dates[dates.length - 1]) : null;
}

const FinancialSummaryTable: React.FC<FinancialSummaryTableProps> = ({
	facts,
	figures: recipeFigures,
	conventions,
	onOpenSource,
	onEditFact,
	onOpenConflict,
}) => {
	// A figure a signal watches is read, never printed (DOP-576).
	const figures = facts.filter((fact) => fact.kind !== "header" && fact.printed !== false);
	// In the order a person dragged them into, when they did (DOP-649).
	const periods = inSavedOrder(periodsOf(figures), orderSetting(conventions, FINANCIAL_COLUMNS), (p) => p);
	const rows = inSavedOrder(
		figureRows(figures).filter((row) => !isAbsentOptional(Array.from(row.cells.values()))),
		orderSetting(conventions, FINANCIAL_ROWS),
		(row) => row.key,
	);
	// A computed cell opens the arithmetic behind it (DOP-527); the inputs
	// are facts of this run, looked up by figure and period.
	const factsBySlot = React.useMemo(() => indexFacts(facts), [facts]);
	const cell = (fact: DealScreeningFact, notFoundLabel?: string) =>
		fact.kind === "derived" ? (
			<DerivedFormulaPopover fact={fact} factsBySlot={factsBySlot} />
		) : (
			<FactValue
				fact={fact}
				variant="table"
				onOpenSource={onOpenSource}
				notFoundLabel={notFoundLabel}
				onEdit={onEditFact}
				onOpenConflict={onOpenConflict}
			/>
		);
	const projected = figures.some((fact) => fact.projected);
	const conflicts = figures.filter((fact) => fact.conflict === "open");
	const converted = figures.filter(
		(fact) => fact.original_currency && fact.fx_rate !== null,
	);
	const unconvertedCurrencies = Array.from(
		new Set(
			figures
				.filter((fact) => fact.original_currency && fact.fx_rate === null && fact.value !== null)
				.map((fact) => fact.original_currency as string),
		),
	);
	// The currency goes once in the header when the whole table shares it.
	const sharedCurrency = tableCurrency(figures);
	const corner = `${currencyMark(sharedCurrency)}mm`;
	const columns = Math.max(periods.length, 1);
	const periodsOfFigure = new Map(
		(recipeFigures ?? []).map((figure) => [figure.key, figure.periods]),
	);
	// With no period found for any figure there is no by-year table to put a
	// row in: everything sits in the one "Latest" table (DOP-633).
	const isPoint = (row: FigureRow) => {
		if (periods.length === 0) return true;
		const declared = periodsOfFigure.get(row.key);
		if (declared) return declared === "point";
		return row.cells.has(null) && !periods.some((period) => row.cells.has(period));
	};
	const seriesRows = rows.filter((row) => !isPoint(row));
	const pointRows = rows.filter(isPoint);

	// A balance is as of a date; "latest" only when no figure states one.
	const asOf = asOfDate(pointRows.flatMap((row) => Array.from(row.cells.values())));
	const balanceHeading =
		seriesRows.length > 0
			? `Balance sheet, ${asOf ? `as of ${asOf}` : "latest"}`
			: asOf
				? `As of ${asOf}`
				: "Latest";

	/** A number cell's content, with room for a negative's ")" when it has none. */
	const numberCell = (fact: DealScreeningFact | undefined) => (
		<>
			{fact ? cell(fact, "—") : "—"}
			{!(fact && fact.value !== null && fact.value < 0) && (
				<span className="invisible" aria-hidden>
					)
				</span>
			)}
		</>
	);
	const rowLabel = (row: FigureRow) => (
		<th scope="row" className="px-4 py-2.5 text-left font-normal text-[#3f4247]">
			{row.label}
			{row.currency && row.currency !== sharedCurrency && (
				<span className="ml-1 font-sans text-[10px] text-text-muted">
					({currencyMark(row.currency)})
				</span>
			)}
			{row.derived && <span className="ml-1.5 font-sans text-[10px] text-text-muted">computed</span>}
		</th>
	);
	const ROW = "border-b border-[#f1f2f4] text-[13.5px] leading-5 text-[#1F2937] last:border-0";

	if (rows.length === 0) {
		return <p className="text-sm text-text-muted">{NOT_FOUND}</p>;
	}

	return (
		<div>
			{/* No period found for any figure: a header row over nothing reads as a
			    broken table, so the figures sit in the one-column table alone. */}
			{seriesRows.length > 0 && (
				<div className="overflow-x-auto rounded-xl border border-[#e7e7e4]">
					<table className="w-full border-collapse font-sans" data-testid="financial-summary">
						<thead>
							<tr className="border-b border-[#e5e7eb] bg-[#fafafa] text-xs text-[#5B6472]">
								<th className="px-4 py-2.5 text-left font-semibold">{corner}</th>
								{periods.map((period) => (
									<th key={period} className="px-4 py-2.5 text-right font-semibold">
										{period}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{seriesRows.map((row) => {
								const cells = Array.from(row.cells.values());
								const allMissing = cells.every(
									(fact) => fact.state === "gap" || fact.state === "not_available",
								);
								return (
									<tr key={row.key} className={ROW}>
										{rowLabel(row)}
										{allMissing ? (
											<td colSpan={columns} className="px-4 py-2.5 text-left text-[#9a9ea3]">
												{cell(cells[0])}
											</td>
										) : (
											periods.map((period) => (
												<td key={period} className="px-4 py-2.5 text-right tabular-nums">
													{numberCell(row.cells.get(period))}
												</td>
											))
										)}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			{pointRows.length > 0 && (
				<div
					className={`${seriesRows.length > 0 ? "mt-3 " : ""}max-w-md overflow-x-auto rounded-xl border border-[#e7e7e4]`}
				>
					<table className="w-full border-collapse font-sans" data-testid="financial-summary-latest">
						<thead>
							<tr className="border-b border-[#e5e7eb] bg-[#fafafa] text-xs text-[#5B6472]">
								<th className="px-4 py-2.5 text-left font-semibold">
									{balanceHeading}
								</th>
								<th className="px-4 py-2.5 text-right font-semibold">{corner}</th>
							</tr>
						</thead>
						<tbody>
							{pointRows.map((row) => (
								<tr key={row.key} className={ROW}>
									{rowLabel(row)}
									<td className="px-4 py-2.5 text-right tabular-nums">
										{numberCell(row.cells.get(null))}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<div className="mt-2.5 space-y-1 font-sans text-[12px] leading-snug text-[#6B7280]" data-testid="financial-notes">
				{projected && conventions.projections && <p>{conventions.projections}</p>}
				{unconvertedCurrencies.length > 0 && (
					<p data-testid="unconverted-note">
						{sharedCurrency
							? `Figures are in ${currencyMarkAndCode(sharedCurrency)}`
							: `Rows marked ${unconvertedCurrencies.map(currencyMark).join(", ")} are stated in that currency`}{" "}
						as the materials report them; no exchange rate and date were found in the
						materials, so they are not converted to USD.
					</p>
				)}
				{conflicts.map((fact) => (
					<p key={`${fact.key}@${fact.period ?? ""}`}>{conflictNote(fact)}</p>
				))}
				{converted.map((fact) => (
					<p key={`fx:${fact.key}@${fact.period ?? ""}`}>
						{fact.label}
						{fact.period ? ` ${fact.period}` : ""} converted from{" "}
						{currencyPrefix(fact.original_currency)}
						{fact.original_value} at {fact.fx_rate}
						{fact.fx_as_of ? ` as of ${formatIsoDay(fact.fx_as_of)}` : ""}.
					</p>
				))}
			</div>
		</div>
	);
};

export default FinancialSummaryTable;
